import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { Pagination } from '@/shared/components/ui/pagination'
import { BusinessHistoryTabSkeleton } from './BusinessDetailSkeletons'
import { safeFormatDateTime } from '@/shared/utils/formatDate'
import { BUSINESS_RECORD_TYPE_LABELS, labelOf } from '@/shared/constants'
import { useBusinessRecords, type BusinessRecord } from '../../hooks/useBusinessRecords'

interface BusinessHistoryTabProps {
  businessId: string
}

function authorName(madeBy: BusinessRecord['madeBy']): string {
  const name = [madeBy.firstName, madeBy.lastName].filter(Boolean).join(' ').trim()
  return name || madeBy.email || 'Sistema'
}

/** `meta` llega como string (posible JSON). Lo muestra legible si se puede. */
function metaText(meta: string): string | null {
  if (!meta) return null
  try {
    const parsed: unknown = JSON.parse(meta)
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed as Record<string, unknown>)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join(' · ')
    }
    return String(parsed)
  } catch {
    return meta
  }
}

export function BusinessHistoryTab({ businessId }: BusinessHistoryTabProps) {
  const { records, totalItems, page, setPage, pageSize, loading, error, refetch } =
    useBusinessRecords(businessId)

  if (error && records.length === 0) {
    return <QueryErrorScreen error={error} onRetry={() => refetch()} />
  }

  if (loading) {
    return <BusinessHistoryTabSkeleton />
  }

  if (records.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-ink-muted">
        Aún no hay movimientos en el historial de este negocio.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <ul className="divide-y divide-line">
        {records.map((record) => {
          const meta = metaText(record.meta)
          return (
            <li key={record.id} className="py-3 first:pt-0">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-ink">
                  {labelOf(BUSINESS_RECORD_TYPE_LABELS, record.type)}
                </span>
                <span className="text-xs text-ink-subtle">
                  {safeFormatDateTime(record.createdAt, '—')}
                </span>
              </div>
              {meta && <p className="mt-1 text-xs text-ink-muted">{meta}</p>}
              <p className="mt-1 text-xs text-ink-subtle">por {authorName(record.madeBy)}</p>
            </li>
          )
        })}
      </ul>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={setPage}
      />
    </div>
  )
}
